import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { EntityMultiCombobox } from "@/components/ui/EntityCombobox";

/**
 * EngineerMultiSelect — searchable multi-select bound to a Set<engineer_id>.
 *
 * Value shape preserved from the pre-Batch 4 dropdown implementation:
 *   - empty Set() = "all engineers"
 *   - non-empty Set() = explicit list
 *   - special "__NONE__..." sentinels represent the deliberate "clear" state
 *     (produced by `engineerFilterToParam` to yield 0 matches on the server).
 */
export default function EngineerMultiSelect({ value, onChange, testId = "eng-multi" }) {
  const [engineers, setEngineers] = useState([]);

  useEffect(() => {
    api.get("/jobs/engineers")
      .then(({ data }) => setEngineers(data || []))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  // Filter out the "__NONE__" sentinels — they exist only to represent an
  // intentional empty match on the wire, they should never render as ticked.
  const cleanValue = Array.from(value || []).filter((id) => !String(id).startsWith("__NONE__"));

  const handleChange = (ids) => onChange(new Set(ids));

  return (
    <EntityMultiCombobox
      items={engineers}
      value={cleanValue}
      onChange={handleChange}
      placeholder="All engineers"
      emptyMessage="No engineers match."
      getLabel={(e) => e.full_name || e.username}
      getSecondary={(e) => e.username}
      summaryLabel={(ids, items) => {
        if (!ids || ids.length === 0) return "All engineers";
        if (ids.length === 1) {
          const only = items.find((i) => i.id === ids[0]);
          return only?.full_name || only?.username || "1 engineer";
        }
        return `${ids.length} engineers`;
      }}
      testid={testId}
      triggerClassName="h-8 w-56 text-xs"
    />
  );
}

/** Convert a Set<id> to a comma-string suitable for engineer_id= param, or omit if empty (= all). */
export function engineerFilterToParam(set) {
  if (!set || set.size === 0) return null;
  const real = Array.from(set).filter((id) => !String(id).startsWith("__NONE__"));
  return real.length ? real.join(",") : "__NONE__";
}
